import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { WidgetProps } from '@/lib/widgets/types';
import { Activity, Cpu, Database, Wifi, Server } from 'lucide-react';

export function SystemPerformanceWidget({ isEditing = false }: WidgetProps) {
  const metrics = {
    cpu: { value: 45, status: 'good', label: 'CPU' },
    memory: { value: 72, status: 'warning', label: 'Memória' },
    disk: { value: 28, status: 'good', label: 'Disco' },
    network: { value: 89, status: 'good', label: 'Rede' },
    database: { value: 34, status: 'good', label: 'Database' }
  };

  const overallStatus = 'operational';
  const uptime = '99.8%';
  const responseTime = '125ms';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'good': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getIcon = (metric: string) => {
    switch (metric) {
      case 'cpu': return <Cpu className="h-3 w-3" />;
      case 'memory': return <Server className="h-3 w-3" />;
      case 'disk': return <Database className="h-3 w-3" />;
      case 'network': return <Wifi className="h-3 w-3" />;
      case 'database': return <Database className="h-3 w-3" />;
      default: return <Activity className="h-3 w-3" />;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Performance do Sistema
          </CardTitle>
          <Badge 
            variant="outline" 
            className="bg-green-50 text-green-700"
          >
            Operacional
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Geral */}
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <p className="text-lg font-bold text-green-600">{uptime}</p>
            <p className="text-xs text-muted-foreground">Uptime</p>
          </div>
          <div>
            <p className="text-lg font-bold text-blue-600">{responseTime}</p>
            <p className="text-xs text-muted-foreground">Resp. Time</p>
          </div>
        </div>

        {/* Métricas */}
        <div className="space-y-3">
          {Object.entries(metrics).map(([key, metric]) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  <span className={getStatusColor(metric.status)}>
                    {getIcon(key)}
                  </span>
                  <span className="font-medium">{metric.label}</span>
                </div>
                <span className={`font-bold ${getStatusColor(metric.status)}`}>
                  {metric.value}%
                </span>
              </div>
              <div className="relative">
                <Progress value={metric.value} className="h-1.5" />
                <div 
                  className={`absolute top-0 left-0 h-1.5 rounded-full ${getProgressColor(metric.status)}`}
                  style={{ width: `${metric.value}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Alertas */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">ALERTAS</span>
            <Badge variant="outline" className="text-xs">
              1 ativo
            </Badge>
          </div>
          <div className="p-2 rounded-lg bg-yellow-50 border border-yellow-200">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-xs font-medium">Uso de memória elevado</span>
            </div>
            <p className="text-xs text-muted-foreground ml-4">
              Recomendamos monitoramento
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}